import { memo, useCallback } from 'react'
import { MagnifyingGlassIcon, GlobeAltIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import SearchResultsDropdown from '../SearchResultsDropdown'
import { trackEvent } from '../../utils/analytics'
import { baseInputClasses } from '../../styles/inputStyles'

/**
 * EntitySearchInput - Search input for finding entities/vendors
 * 
 * Features:
 * - Text input with search icon
 * - Loading spinner state
 * - Dropdown results integration
 * - Focus/blur handling
 * - No results message
 * - Analytics tracking for user interactions
 * 
 * @idempotent - Controlled component with external state
 * @atomic - Single purpose: entity search input
 */
const EntitySearchInput = memo(function EntitySearchInput({
  value,
  onChange,
  onFocus,
  inputRef,
  isSearching = false,
  searchResults = [],
  showDropdown = false,
  onSelectEntity,
  onCloseDropdown,
  showNoResults = false,
  userLocation = null,
  searchLocation = '',
  searchLocationName = '',
  radius = 50,
  onClearLocation = null,
  size = 'default', // 'default' or 'compact'
  className = ''
}) {
  const { t } = useTranslation()

  const handleFocus = useCallback(() => {
    trackEvent('Search', 'entity_input_focus', 'search_tab', null, {
      location: 'search_tab',
      current_value: value || '',
      has_value: value?.trim().length > 0,
      has_location: !!userLocation
    })
    if (onFocus) onFocus()
  }, [value, userLocation, onFocus])

  const handleBlur = useCallback(() => {
    trackEvent('Search', 'entity_input_blur', 'search_tab', null, {
      location: 'search_tab',
      final_value: value || '',
      has_value: value?.trim().length > 0,
      value_length: value?.length || 0,
      has_results: searchResults.length > 0,
      results_count: searchResults.length
    })
  }, [value, searchResults])

  const sizeClasses = size === 'compact' 
    ? 'px-3 py-2 pr-10 text-sm'
    : 'px-4 py-2 pr-10'

  const iconSizeClass = size === 'compact' ? 'h-4 w-4' : 'h-5 w-5'

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        id="entity-search"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={t('map.truckPlaceholder')}
        className={`${baseInputClasses} ${sizeClasses} pr-10`}
      />
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        {isSearching ? (
          <div className={`animate-spin rounded-full ${iconSizeClass} border-b-2 border-primary`}></div>
        ) : (
          <MagnifyingGlassIcon className={`${iconSizeClass} text-gray-400`} />
        )}
      </div>

      {/* Global results banner when no location filter is active */}
      {value && value.trim().length > 0 && (!searchLocation || !searchLocation.trim()) && (
        <div className="flex items-center gap-1.5 text-xs text-blue-600 mt-1 px-1">
          <GlobeAltIcon className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{t('search.globalResults', 'Showing all results — no location filter')}</span>
        </div>
      )}

      {/* Search Results Dropdown */}
      <SearchResultsDropdown
        results={searchResults}
        onSelectEntity={onSelectEntity}
        showDropdown={showDropdown}
        inputRef={inputRef}
        onClose={onCloseDropdown}
        searchLocation={searchLocation}
        searchLocationName={searchLocationName}
        radius={radius}
        onClearLocation={onClearLocation}
        query={value}
      />

      {/* No Results Message */}
      {showNoResults && (
        <div className="absolute z-[500] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4">
          <p className="text-sm text-gray-500 text-center">
            {t('map.noTrucksFound', { query: value })}
            {!userLocation && (
              <span className="block mt-1 text-xs text-gray-400">
                {t('map.locationTip')}
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  )
})

export default EntitySearchInput
